import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@/hooks/useWallet';
import { useContract } from '@/hooks/useContract';
import { useToast } from '@/contexts/ToastContext';
import { CharacterClass, CLASS_INFO } from '@/lib/types/game';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';

const NewGame: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected } = useWallet();
  const { createGame } = useContract();
  const toast = useToast();

  const [selectedClasses, setSelectedClasses] = useState<(CharacterClass | null)[]>([null, null, null, null]);
  const [isCreating, setIsCreating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Redirect if not connected
  React.useEffect(() => {
    if (!isConnected) {
      navigate('/');
    }
  }, [isConnected, navigate]);

  const handleClassSelect = (index: number, classType: CharacterClass) => {
    const newClasses = [...selectedClasses];
    newClasses[index] = classType;
    setSelectedClasses(newClasses);
  };

  const canCreateGame = selectedClasses.every((c) => c !== null);

  const handleCreateGame = async () => {
    if (!canCreateGame) {
      toast.error('Please select a class for all 4 characters');
      return;
    }

    setIsCreating(true);
    setShowConfirm(false);

    try {
      const classes = selectedClasses as [CharacterClass, CharacterClass, CharacterClass, CharacterClass];
      const success = await createGame(classes);

      if (success) {
        toast.success('Your adventure begins!');
        setTimeout(() => navigate('/game'), 2000);
      }
    } catch (error) {
      console.error('Failed to create game:', error);
      toast.error('Failed to create game. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const classOptions = [
    { class: CharacterClass.FIGHTER, color: 'bg-red-600', icon: '⚔️' },
    { class: CharacterClass.THIEF, color: 'bg-purple-600', icon: '🗡️' },
    { class: CharacterClass.MONK, color: 'bg-orange-600', icon: '👊' },
    { class: CharacterClass.RED_MAGE, color: 'bg-pink-600', icon: '🔮' },
    { class: CharacterClass.WHITE_MAGE, color: 'bg-blue-400', icon: '✨' },
    { class: CharacterClass.BLACK_MAGE, color: 'bg-indigo-700', icon: '🌟' },
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#1a1a2e] to-[#0f0e17] p-4">
      <div className="max-w-6xl mx-auto py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-pixel-lg text-[#e94560] text-shadow">Create Your Party</h1>
          <Button variant="secondary" onClick={() => navigate('/')}>
            Back
          </Button>
        </div>

        {/* Instructions */}
        <Card className="mb-8">
          <p className="text-game text-xl mb-4">
            Choose your party composition carefully. Each class has unique strengths and weaknesses.
            A balanced party is recommended for beginners.
          </p>
          <p className="text-game text-lg text-[#e94560]">
            Classic Party: Fighter, Thief, White Mage, Black Mage
          </p>
        </Card>

        {/* Party Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {selectedClasses.map((selectedClass, index) => (
            <Card key={index} title={`Character ${index + 1}`}>
              <div className="space-y-3">
                {classOptions.map(({ class: classType, color, icon }) => (
                  <button
                    key={classType}
                    onClick={() => handleClassSelect(index, classType)}
                    className={`
                      w-full p-3 ff-border text-left transition-all
                      ${selectedClass === classType ? `${color} scale-105` : 'bg-[#0f3460] hover:bg-[#16213e]'}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{icon}</span>
                      <span className="text-pixel-sm">{CLASS_INFO[classType].name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* Selected classes details */}
        {canCreateGame && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {selectedClasses.map((classType, index) => {
              if (classType === null) return null;
              const info = CLASS_INFO[classType];
              return (
                <Card key={index} className="bg-[#0f3460]/50">
                  <h3 className="text-pixel-sm text-[#e94560] mb-2">{info.name}</h3>
                  <p className="text-game text-sm">{info.description}</p>
                </Card>
              );
            })}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-center gap-4">
          <Button
            variant="primary"
            size="lg"
            onClick={() => setShowConfirm(true)}
            disabled={!canCreateGame}
            isLoading={isCreating}
          >
            {isCreating ? 'Creating Game...' : 'Begin Adventure'}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setSelectedClasses([null, null, null, null])}
            disabled={isCreating}
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Confirmation modal */}
      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Confirm Party">
        <div className="space-y-4">
          <p className="text-game text-lg">
            Are you ready to begin your adventure with this party?
          </p>
          <div className="space-y-2">
            {selectedClasses.map((classType, index) => {
              if (classType === null) return null;
              return (
                <div key={index} className="ff-border px-3 py-2 bg-[#0f3460]">
                  <span className="text-pixel-sm">
                    Character {index + 1}: {CLASS_INFO[classType].name}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="secondary" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateGame}>
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default NewGame;
